import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Building2, Package, Target, TrendingUp, Coins } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const StatBox = ({ icon: Icon, label, value, bg, testid }) => (
  <div className="rounded-2xl p-3 text-white text-center shadow-lg" style={{ background: bg }} data-testid={testid}>
    <Icon className="w-5 h-5 mx-auto opacity-90" />
    <p className="text-[10px] font-bold opacity-90 mt-1">{label}</p>
    <p className="font-black text-xl leading-tight" dir="ltr">{value}</p>
  </div>
);

const BrandStats = ({ b }) => (
  <div className="grid grid-cols-3 gap-2" data-testid="mandoob-brand-stats">
    <StatBox icon={Target} label="التارجت (كرتون)" value={fmt(b.target_units)} bg="linear-gradient(135deg,#F59E0B,#B45309)" testid="brand-target-box" />
    <StatBox icon={TrendingUp} label={b.target_reached ? "مبيوع ✓ حققته" : "مبيوع بالدورة"} value={fmt(b.sold_units)}
      bg={b.target_reached ? "linear-gradient(135deg,#22C55E,#15803D)" : "linear-gradient(135deg,#EF4444,#B91C1C)"} testid="brand-sold-box" />
    <StatBox icon={Coins} label="ربحك لكل كرتون" value={fmt(b.profit_per_unit)} bg="linear-gradient(135deg,#3B82F6,#1E3A8A)" testid="brand-rate-box" />
    <p className="col-span-3 text-center text-xs font-black text-emerald-300" data-testid="brand-earned">
      أرباحك من هاي الشركة: <span dir="ltr">{fmt(b.earned)}</span> د.ع
    </p>
  </div>
);

const BrandProducts = ({ b, onBack }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/mandoob/brands/${b.brand_id}/products`).then(({ data }) => setItems(data)).catch(() => toast.error("فشل التحميل"));
  }, [b.brand_id]);
  return (
    <div className="space-y-4">
      <button onClick={onBack} data-testid="mandoob-brand-back" className="text-white text-sm font-bold flex items-center gap-1">
        <ChevronLeft className="w-4 h-4 rotate-180" /> الشركات
      </button>
      <h2 className="text-white font-black text-2xl">{b.name}</h2>
      <BrandStats b={b} />
      {!items ? <Loader2 className="w-6 h-6 animate-spin text-white mx-auto" /> : (
        <div className="grid grid-cols-2 gap-3" data-testid="mandoob-brand-products">
          {items.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl p-2 shadow" data-testid={`mandoob-brand-product-${p.id}`}>
              {p.image_path ? <img src={`${API}/files/${p.image_path}`} alt="" className="w-full aspect-square object-contain rounded-xl bg-slate-50" loading="lazy" /> :
                <div className="w-full aspect-square rounded-xl bg-slate-100 flex items-center justify-center"><Package className="w-8 h-8 text-slate-400" /></div>}
              <p className="text-xs font-bold mt-1.5 line-clamp-2 min-h-[2rem] text-[#0B1E4A]">{p.name_ar}</p>
              <p className="font-black text-sm text-[#1E3A8A]" dir="ltr">{fmt(p.price)} د.ع</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MandoobBrands = ({ onBack }) => {
  const [brands, setBrands] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    axios.get(`${API}/mandoob/brands`).then(({ data }) => setBrands(data)).catch(() => toast.error("فشل تحميل الشركات"));
  }, []);
  return (
    <div className="-mx-3 -my-4 px-4 py-6 min-h-[calc(100vh-60px)] space-y-4" dir="rtl" data-testid="mandoob-brands"
      style={{ background: "radial-gradient(circle at top, #1E3A8A 0%, #0B1E4A 55%, #050F2C 100%)" }}>
      {open ? <BrandProducts b={open} onBack={() => setOpen(null)} /> : (
        <>
          <button onClick={onBack} data-testid="mandoob-brands-back" className="text-white text-sm font-bold flex items-center gap-1">
            <ChevronLeft className="w-4 h-4 rotate-180" /> الرئيسية
          </button>
          <h2 className="text-white font-black text-2xl">الشركات</h2>
          {!brands ? <Loader2 className="w-6 h-6 animate-spin text-white mx-auto" /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {brands.map((b) => (
                <button key={b.brand_id} onClick={() => setOpen(b)} data-testid={`mandoob-brand-${b.brand_id}`}
                  className="relative bg-white/10 border border-white/15 rounded-3xl p-4 flex flex-col items-center gap-2 text-white active:scale-95 transition-transform">
                  <span data-testid={`mandoob-brand-badge-${b.brand_id}`}
                    className={`absolute -top-2 -left-2 min-w-[32px] h-8 px-1.5 rounded-full text-xs font-black flex items-center justify-center shadow-lg ring-2 ring-[#0B1E4A] ${b.target_reached ? "bg-emerald-500" : "bg-red-500"}`} dir="ltr">
                    {fmt(b.sold_units)}
                  </span>
                  {b.logo ? <img src={`${API}/files/${b.logo}`} alt="" className="w-16 h-16 rounded-2xl object-cover bg-white" /> :
                    <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center"><Building2 className="w-8 h-8" /></div>}
                  <p className="font-black text-sm text-center">{b.name}</p>
                  {b.target_units > 0 && <p className="text-[10px] text-blue-200 font-bold" dir="ltr">{fmt(b.sold_units)} / {fmt(b.target_units)}</p>}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MandoobBrands;
